// Small pieces shared by the tense pages: the ending chip (terracotta for सामान्य, outlined for विकल्प).

import type { FC, ReactNode } from 'react'
import type { PratyayaForm } from '@/prakrit/lib/grammar/pratyaya'

import { cn } from '@/lib/utils'
import { endingLabel } from '@/prakrit/lib/grammar/pratyaya'

export type ChipTone = 'samanya' | 'vikalp'

const TONE: Record<ChipTone, string> = {
    samanya: 'border-terracotta bg-terracotta text-background shadow-sm',
    vikalp: 'border-terracotta/70 bg-terracotta/8 text-terracotta'
}

interface PratyayaChipProps {
    form: Pick<PratyayaForm, 'ending' | 'marker'>
    tone: ChipTone
    size?: 'sm' | 'md'
    className?: string
}

/** One ending, e.g. मि, इत्था, ईअ + मि, Ø (the bare root). */
export const PratyayaChip: FC<PratyayaChipProps> = ({ form, tone, size = 'md', className }): ReactNode => {
    const label = endingLabel(form)

    return (
        <span
            lang='pra'
            title={label === '०' ? 'शून्य प्रत्यय: धातु ही · the zero ending: the bare root' : undefined}
            className={cn(
                'inline-flex items-center justify-center rounded-xl border font-prakrit font-semibold leading-none',
                size === 'md' ? 'min-h-11 min-w-11 px-3 text-[1.0625rem]' : 'min-h-8 min-w-8 px-2.5 text-sm',
                TONE[tone],
                className
            )}
        >
            {label}
        </span>
    )
}

/** The distinct ending labels of a list of forms, in order (one chip per ending). */
export const distinctEndings = <T extends Pick<PratyayaForm, 'ending' | 'marker'>>(forms: T[]): T[] => {
    const seen = new Set<string>()

    return forms.filter(f => {
        const key = endingLabel(f)

        if (seen.has(key)) return false

        seen.add(key)

        return true
    })
}
